/***
 * Custom Logic to override Upcart Logic and template for specific actions
 * TPS-517: On the PDP, add in customization related to restricting the quantities for specific products based on the
 *          product metafield (minimum_sale_qty)
 */
document.addEventListener('DOMContentLoaded', () => {
  initializeUpcartCustomization();
});

// Track in-flight requests by itemId to prevent race conditions
const pendingRequests = new Map();
const upcartProductRequestCache = new Map();
let upcartInitialSyncTimer = null;
let upcartInitialSyncResolver = null;
let upcartCheckoutLockCount = 0;
let lastUpcartLineSignature = '';
let lastUpcartDrawerOpenSyncAt = 0;
let upcartCartOpenedUnsubscribe = null;
let hasUpcartCartOpenedEventListener = false;
let upcartDrawerOpenSyncPromise = null;

// Keep page-load sync as the default behavior and use this as the master switch
// for drawer-open refreshes. When enabled, the cart page refreshes on every
// Upcart open, while other pages still respect the cooldown below.
const ENABLE_UPCART_SYNC_ON_PAGE_LOAD = true;
const ENABLE_UPCART_SYNC_ON_DRAWER_OPEN = true;
// Only applies away from the cart page. Cart-page opens bypass this cooldown so
// the drawer always re-syncs against the current cart view.
const UPCART_DRAWER_OPEN_SYNC_COOLDOWN_MS = 60000;

// Phase 2: one in-flight +/- handler per cart line tile (prevents UpCart native handler on double-click)
const quantityTileClickInFlight = new WeakSet();
// TPS-641 Upcart Availability Message should properly update even when null ATP date
const availabilityPersistInFlight = new Set();

function initializeUpcartCustomization() {
  subscribeToUpcartOpenedEvent();

  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      if (mutation.type === 'childList') {
        hideUnderscoreProperties();
        styleAvailabilityMessages();
        syncUpcartCheckoutButtonState();

        // Add a small delay to ensure elements are fully loaded
        setTimeout(() => {
          const upcart = document.getElementById('upCart');
          if (upcart) {
            const nextSignature = getUpcartLineSignature(upcart);
            if (nextSignature !== lastUpcartLineSignature) {
              lastUpcartLineSignature = nextSignature;
              getCurrentUpcartProductItems(upcart).forEach((item) => {
                setupProductItemListeners(item);
              });
              queueInitialUpcartAvailabilitySync();
            }
          }
          styleAvailabilityMessages();
          syncUpcartCheckoutButtonState();
        }, 100);
      }
    });
  });

  // Start observing the Upcart
  const upcart = document.getElementById('upCart');
  if (upcart) {
    observer.observe(upcart, { childList: true, subtree: true });
    hideUnderscoreProperties();
    styleAvailabilityMessages();

    getCurrentUpcartProductItems(upcart).forEach(item => {
      setupProductItemListeners(item);
    });
    lastUpcartLineSignature = getUpcartLineSignature(upcart);
    styleAvailabilityMessages();
    syncUpcartCheckoutButtonState();
    if (ENABLE_UPCART_SYNC_ON_PAGE_LOAD) {
      queueInitialUpcartAvailabilitySync();
    }
  }
}

/**
 * Hide properties starting with underscore prefix but keep them on the page so their data is accessible
 *
 * Important! Do not enable upcart's advanced cart option 'Hide properties starting with single underscore "_"' as this
 * will prevent them from rendering on the page and we won't have access to the values when they first get added to cart
 *
 * Updated for V2.0: Uses .upcart-public-product-properties__item class
 */
function hideUnderscoreProperties() {
  // V2.0 class
  const properties = document.querySelectorAll('.upcart-public-product-properties__item');
  // Fallback for V1.0 compatibility
  const v1Properties = document.querySelectorAll('.upcart-item-property');

  [...properties, ...v1Properties].forEach(property => {
    const text = property.textContent.trim();
    if (text.startsWith('_')) {
      property.classList.add('hidden');
    }
  });
}

function isUnderscorePropertyKeyValueText(text) {
  const trimmed = String(text || '').trim();
  if (!trimmed || trimmed.startsWith('_')) {
    return true;
  }

  const colonIndex = trimmed.indexOf(':');
  if (colonIndex > 0) {
    return trimmed.substring(0, colonIndex).trim().startsWith('_');
  }

  return false;
}

// TPS-641 Upcart Availability Message should properly update even when null ATP date
/**
 * Locate the shopper-visible Availability value node on an UpCart line tile.
 */
function findAvailabilityMessageElement(productItem) {
  if (!productItem) return null;

  const marked = productItem.querySelector('.availability-message-text');
  if (marked) {
    return marked;
  }

  const v2PropertyItems = productItem.querySelectorAll('.upcart-public-product-properties__item');
  for (const propertyItem of v2PropertyItems) {
    if (propertyItem.classList.contains('hidden')) {
      continue;
    }

    const keyValuePair = propertyItem.querySelector('.upcart-public-cart-items__key-value-pair');
    if (!keyValuePair) {
      continue;
    }

    const text = keyValuePair.textContent.trim();
    if (!text || isUnderscorePropertyKeyValueText(text)) {
      continue;
    }

    return keyValuePair;
  }

  const v1PropertyItems = productItem.querySelectorAll('.upcart-item-property');
  for (const propertyItem of v1PropertyItems) {
    if (propertyItem.classList.contains('hidden')) {
      continue;
    }

    const strongElement = propertyItem.querySelector('strong');
    const spanElement = propertyItem.querySelector('span');
    if (!strongElement || !spanElement) {
      continue;
    }

    const key = strongElement.textContent.replace(':', '').trim();
    if (key.startsWith('_')) {
      continue;
    }

    return spanElement;
  }

  return null;
}

/**
 * TPS-593: Remove "Availability: " label from availability messages
 * Works with Upcart V2.0 classes
 */
function styleAvailabilityMessages() {
  const upcart = document.getElementById('upCart');
  if (upcart) {
    getCurrentUpcartProductItems(upcart).forEach((productItem) => {
      const availabilityElement = findAvailabilityMessageElement(productItem);
      if (availabilityElement) {
        availabilityElement.classList.add('availability-message-text');
      }
    });
  }

  const availabilityElements = document.querySelectorAll('.upcart-public-cart-items__key-value-pair');

  availabilityElements.forEach(element => {
    const text = element.textContent.trim();
    const parentItem = element.closest('.upcart-public-product-properties__item');
    const parentText = parentItem ? parentItem.textContent.trim() : '';
    if (text.startsWith('Availability:')) {
      const newText = text.replace(/^Availability:\s*/i, '');
      if (newText !== text) {
        element.textContent = newText;
      }
      element.classList.add('availability-message-text');
    } else if (parentText.includes('Availability')) {
      element.classList.add('availability-message-text');
    }
  });
}

function getUpcartCheckoutButton() {
  return document.querySelector('#upCart .upcart-checkout-button') || document.querySelector('.upcart-checkout-button');
}

function syncUpcartCheckoutButtonState() {
  const checkoutButton = getUpcartCheckoutButton();
  if (!checkoutButton) return;

  const isLocked = upcartCheckoutLockCount > 0;
  if (isLocked) {
    if (!checkoutButton.dataset.originalHref && checkoutButton.getAttribute('href')) {
      checkoutButton.dataset.originalHref = checkoutButton.getAttribute('href');
    }

    checkoutButton.removeAttribute('href');
    checkoutButton.setAttribute('aria-disabled', 'true');
    checkoutButton.setAttribute('tabindex', '-1');
    checkoutButton.classList.add('upcart-checkout-button--disabled');
    checkoutButton.style.pointerEvents = 'none';
    checkoutButton.style.opacity = '0.6';
    checkoutButton.style.cursor = 'not-allowed';
    return;
  }

  if (checkoutButton.dataset.originalHref) {
    checkoutButton.setAttribute('href', checkoutButton.dataset.originalHref);
  }

  checkoutButton.removeAttribute('aria-disabled');
  checkoutButton.removeAttribute('tabindex');
  checkoutButton.classList.remove('upcart-checkout-button--disabled');
  checkoutButton.style.pointerEvents = '';
  checkoutButton.style.opacity = '';
  checkoutButton.style.cursor = '';
}

function beginUpcartCheckoutLock() {
  upcartCheckoutLockCount += 1;
  syncUpcartCheckoutButtonState();
}

function endUpcartCheckoutLock() {
  upcartCheckoutLockCount = Math.max(0, upcartCheckoutLockCount - 1);
  syncUpcartCheckoutButtonState();
}

function normalizePathname(pathname) {
  const normalizedPathname = String(pathname || '').replace(/\/+$/, '');
  return normalizedPathname || '/';
}

function isCartPage() {
  const configuredCartUrl = window?.routes?.cart_url || '/cart';

  try {
    return normalizePathname(window.location.pathname) === normalizePathname(new URL(configuredCartUrl, window.location.origin).pathname);
  } catch (error) {
    return normalizePathname(window.location.pathname) === normalizePathname(configuredCartUrl);
  }
}

function waitForTimeout(delayMs) {
  return new Promise((resolve) => {
    window.setTimeout(resolve, delayMs);
  });
}

async function waitForUpcartProductItems(options = {}) {
  const { attempts = 10, delayMs = 100 } = options;

  for (let attempt = 0; attempt < attempts; attempt += 1) {
    const upcart = document.getElementById('upCart');
    const productItems = getCurrentUpcartProductItems(upcart);
    if (productItems.length > 0) {
      return productItems;
    }

    await waitForTimeout(delayMs);
  }

  return [];
}

async function syncUpcartPreloadedCartFromShopify() {
  try {
    const response = await fetch('/cart.js');
    if (!response.ok) {
      throw new Error(`Failed to fetch latest cart state: ${response.status}`);
    }

    const cart = await response.json();
    if (window.upcartPreloadedCart && typeof window.upcartPreloadedCart === 'object') {
      window.upcartPreloadedCart.items = Array.isArray(cart.items) ? cart.items : [];
    } else {
      window.upcartPreloadedCart = cart;
    }

    return cart;
  } catch (error) {
    console.error('Error syncing UpCart preloaded cart from Shopify:', error);
    return null;
  }
}

async function refreshUpcartDrawerContents() {
  await syncUpcartPreloadedCartFromShopify();

  if (typeof window.upcartRefreshCart === 'function') {
    try {
      const refreshResult = window.upcartRefreshCart();
      if (refreshResult && typeof refreshResult.then === 'function') {
        await refreshResult;
      }
    } catch (error) {
      console.error('Error refreshing UpCart drawer contents:', error);
    }
  }

  await waitForUpcartProductItems({ attempts: 20, delayMs: 100 });
}

// Coalesce bursts of UpCart re-renders into one availability sync, while
// resolving any previously queued caller once its pending run is superseded.
function queueInitialUpcartAvailabilitySync(options = {}) {
  const { forceFreshData = false } = options;

  if (upcartInitialSyncTimer) {
    clearTimeout(upcartInitialSyncTimer);
    upcartInitialSyncTimer = null;
  }

  if (typeof upcartInitialSyncResolver === 'function') {
    upcartInitialSyncResolver();
    upcartInitialSyncResolver = null;
  }

  return new Promise((resolve) => {
    upcartInitialSyncResolver = resolve;

    upcartInitialSyncTimer = setTimeout(() => {
      upcartInitialSyncTimer = null;

      runInitialUpcartAvailabilitySync({ forceFreshData })
        .catch((error) => {
          console.error('Error syncing initial UpCart availability:', error);
        })
        .finally(() => {
          if (upcartInitialSyncResolver === resolve) {
            upcartInitialSyncResolver = null;
          }

          resolve();
        });
    }, 150);
  });
}

function shouldRunUpcartDrawerOpenSync() {
  if (!ENABLE_UPCART_SYNC_ON_DRAWER_OPEN) {
    return false;
  }

  const now = Date.now();
  if (isCartPage()) {
    lastUpcartDrawerOpenSyncAt = now;
    return true;
  }

  if (now - lastUpcartDrawerOpenSyncAt < UPCART_DRAWER_OPEN_SYNC_COOLDOWN_MS) {
    return false;
  }

  lastUpcartDrawerOpenSyncAt = now;
  return true;
}

function getCurrentUpcartProductItems(upcart = document.getElementById('upCart')) {
  if (!upcart) return [];

  const productItems = upcart.querySelectorAll('.upcart-public-component-product-tile');
  const v1ProductItems = upcart.querySelectorAll('.upcart-product-item');

  return [...productItems, ...v1ProductItems];
}

function getUpcartLineSignature(upcart = document.getElementById('upCart')) {
  return getCurrentUpcartProductItems(upcart)
    .map((productItem) => {
      const itemId =
        productItem.id ||
        productItem.dataset.itemId ||
        productItem.dataset.itemKey ||
        getItemIdFromProductTileSync(productItem) ||
        '';
      const variantId = getVariantIdFromTile(productItem) || '';
      const quantity = getCurrentQuantityFromProductTile(productItem) || 0;

      return `${itemId}|${variantId}|${quantity}`;
    })
    .sort()
    .join('||');
}

async function queueUpcartDrawerOpenSync(options = {}) {
  const { forceFreshData = false } = options;
  const upcart = document.getElementById('upCart');
  if (!upcart) return;

  let productItems = getCurrentUpcartProductItems(upcart);
  if (productItems.length === 0) {
    productItems = await waitForUpcartProductItems({ attempts: 10, delayMs: 100 });
  }

  if (productItems.length === 0) return;

  productItems.forEach((item) => {
    setupProductItemListeners(item);
  });
  lastUpcartLineSignature = getUpcartLineSignature(upcart);
  return queueInitialUpcartAvailabilitySync({ forceFreshData });
}

function runUpcartDrawerOpenSync(options = {}) {
  if (upcartDrawerOpenSyncPromise) {
    return upcartDrawerOpenSyncPromise;
  }

  upcartDrawerOpenSyncPromise = (async () => {
    beginUpcartCheckoutLock();

    if (isCartPage()) {
      await refreshUpcartDrawerContents();
    }

    await queueUpcartDrawerOpenSync(options);
  })().finally(() => {
    endUpcartCheckoutLock();
    upcartDrawerOpenSyncPromise = null;
  });

  return upcartDrawerOpenSyncPromise;
}

function subscribeToUpcartOpenedEvent() {
  const handleCartOpened = () => {
    if (!shouldRunUpcartDrawerOpenSync()) return;
    //console.debug("DEBUG: Running Upcart Drawer Open fired");
    runUpcartDrawerOpenSync({ forceFreshData: true }).catch((error) => {
      console.error('Error syncing UpCart drawer on open:', error);
    });
  };

  // Prefer UpCart's public subscription helper for the drawer-open event, and
  // fall back to the raw browser event only if the helper is unavailable.
  if (!upcartCartOpenedUnsubscribe && typeof window.upcartSubscribeCartOpened === 'function') {
    upcartCartOpenedUnsubscribe = window.upcartSubscribeCartOpened(handleCartOpened);
    return;
  }

  if (!hasUpcartCartOpenedEventListener) {
    window.addEventListener('aftersell-upcart:public-events:cart-opened', handleCartOpened);
    hasUpcartCartOpenedEventListener = true;
  }
}

function getProductHandleFromTile(productItem) {
  const productLink = productItem?.querySelector(
    '.upcart-product-title-link, .upcart-public-component-product-tile__title-link'
  );
  if (!productLink?.href || !productLink.href.includes('/products/')) {
    return null;
  }

  return productLink.href.split('/products/')[1].split('?')[0];
}

function getVariantIdFromTile(productItem) {
  const productLink = productItem?.querySelector(
    '.upcart-product-title-link, .upcart-public-component-product-tile__title-link'
  );
  if (!productLink?.href) return null;

  try {
    return new URL(productLink.href).searchParams.get('variant');
  } catch (error) {
    return null;
  }
}

function getVariantIdForProductTile(productItem, itemId) {
  if (itemId && window?.upcartPreloadedCart?.items?.length > 0) {
    const cartItem = window.upcartPreloadedCart.items.find((entry) => entry.key === itemId);
    const cartVariantId = cartItem?.variant_id || cartItem?.variant?.id;
    if (cartVariantId) {
      return String(cartVariantId);
    }
  }

  const tileVariantId = getVariantIdFromTile(productItem);
  return tileVariantId ? String(tileVariantId) : null;
}

function getCurrentQuantityFromProductTile(productItem) {
  const quantitySelector = productItem?.querySelector('.upcart-public-component-product-tile__quantity-selector');
  const quantityInput = quantitySelector
    ? quantitySelector.querySelector('input[inputmode="numeric"], input[type="text"], input[type="number"]')
    : productItem?.querySelector('.upcart-product-quantity-input');

  return quantityInput ? parseInt(quantityInput.value, 10) || 0 : 0;
}

// TPS-631: Keep UpCart tile id aligned with Shopify line keys after property persist
// or AJAX cart updates rotate the line item key.
function isCartLineKeyInPreloadedCart(key) {
  if (!key) return false;

  return Boolean(
    window?.upcartPreloadedCart?.items?.some((item) => item.key === key)
  );
}

function findCartLineKeyForVariantId(variantId, items = window?.upcartPreloadedCart?.items) {
  if (!variantId || !Array.isArray(items)) return null;

  const item = items.find((entry) => {
    const entryVariantId = entry.variant_id || entry.variant?.id;
    return String(entryVariantId) === String(variantId);
  });

  return item?.key || null;
}

async function findLiveCartLineKey(itemId, productItem = null) {
  const cart = await syncUpcartPreloadedCartFromShopify();
  if (!cart?.items?.length) return null;

  const exactMatch = cart.items.find((item) => item.key === itemId);
  if (exactMatch?.key) return exactMatch.key;

  const variantPart = itemId?.includes(':') ? itemId.split(':')[0] : null;
  if (variantPart) {
    const byVariant = cart.items.find((item) => String(item.variant_id) === String(variantPart));
    if (byVariant?.key) return byVariant.key;
  }

  if (productItem) {
    const variantId = getVariantIdFromTile(productItem);
    if (variantId) {
      return findCartLineKeyForVariantId(variantId, cart.items);
    }
  }

  return null;
}

function syncProductTileItemId(productItem, itemId) {
  if (!productItem || !itemId) return;

  productItem.id = itemId;
  productItem.dataset.itemId = itemId;
  productItem.dataset.itemKey = itemId;
}

function migrateUpcartLineKeyReferences(oldItemId, newItemId) {
  if (!oldItemId || !newItemId || oldItemId === newItemId) return;

  const tile = findProductTileByItemId(oldItemId);
  if (tile) {
    syncProductTileItemId(tile, newItemId);
  }

  if (pendingRequests.has(oldItemId)) {
    pendingRequests.set(newItemId, true);
    pendingRequests.delete(oldItemId);
  }

  if (availabilityPersistInFlight.has(oldItemId)) {
    availabilityPersistInFlight.delete(oldItemId);
    availabilityPersistInFlight.add(newItemId);
  }
}

function resolveProductTileItemIdFromPreloadedCart(productItem) {
  if (!productItem) return null;

  const domCandidate =
    productItem.id || productItem.dataset.itemId || productItem.dataset.itemKey || null;

  if (domCandidate && isCartLineKeyInPreloadedCart(domCandidate)) {
    syncProductTileItemId(productItem, domCandidate);
    return domCandidate;
  }

  const variantId = getVariantIdFromTile(productItem);
  if (!variantId) {
    return null;
  }

  const resolvedKey = findCartLineKeyForVariantId(variantId);
  if (resolvedKey) {
    syncProductTileItemId(productItem, resolvedKey);
    return resolvedKey;
  }

  return null;
}

function updateHiddenPropertyValueInDOM(productItem, propertyName, propertyValue) {
  const value = String(propertyValue ?? '');

  const v2PropertyElements = productItem.querySelectorAll('.upcart-public-product-properties__item');
  for (const propertyElement of v2PropertyElements) {
    const keyValuePair = propertyElement.querySelector('.upcart-public-cart-items__key-value-pair');
    if (keyValuePair && keyValuePair.textContent.trim().startsWith(`${propertyName}:`)) {
      keyValuePair.textContent = `${propertyName}: ${value}`;
      return true;
    }
  }

  const v1PropertyElements = productItem.querySelectorAll('.upcart-item-property');
  for (const propertyElement of v1PropertyElements) {
    const strongElement = propertyElement.querySelector('strong');
    const spanElement = propertyElement.querySelector('span');
    if (strongElement && spanElement && strongElement.textContent.replace(':', '').trim() === propertyName) {
      spanElement.textContent = value;
      return true;
    }
  }

  return false;
}

function updatePreloadedCartProperty(itemId, propertyName, propertyValue) {
  const item = window?.upcartPreloadedCart?.items?.find((entry) => entry.key === itemId);
  if (!item) return;

  item.properties = item.properties || {};
  item.properties[propertyName] = String(propertyValue ?? '');
}

function getUpcartProductJson(handle, options = {}) {
  const { forceRefresh = false } = options;

  if (!handle) {
    return Promise.reject(new Error('Missing product handle'));
  }

  if (forceRefresh) {
    upcartProductRequestCache.delete(handle);
  }

  if (!upcartProductRequestCache.has(handle)) {
    upcartProductRequestCache.set(
      handle,
      fetch(`/products/${handle}.js`)
        .then((response) => {
          if (!response.ok) {
            throw new Error(`Product lookup failed for ${handle}`);
          }

          return response.json();
        })
        .catch((error) => {
          upcartProductRequestCache.delete(handle);
          throw error;
        })
    );
  }

  return upcartProductRequestCache.get(handle);
}

async function syncInitialAvailabilityForProductTile(productItem, options = {}) {
  const { forceFreshData = false } = options;
  const itemId = await getItemIdFromProductTile(productItem);
  if (!itemId) return;

  const properties = getProperties(itemId) || {};
  const hasAvailabilityContract =
    Object.prototype.hasOwnProperty.call(properties, '_product_sku') ||
    Object.prototype.hasOwnProperty.call(properties, '_lead_time') ||
    Object.prototype.hasOwnProperty.call(properties, '_is_custom_order') ||
    Object.prototype.hasOwnProperty.call(properties, '_variant_available_quantity');

  if (!hasAvailabilityContract) {
    syncMinQtyQuantityControls(productItem);
    return;
  }

  let availableQty = Number(properties._variant_available_quantity);
  if (!Number.isFinite(availableQty)) {
    availableQty = 0;
  }

  const storedQty = String(properties._variant_available_quantity ?? "");
  const storedAvailability = String(properties.Availability || "").trim();

  const productHandle = getProductHandleFromTile(productItem);
  const variantId = getVariantIdForProductTile(productItem, itemId);
  if (variantId) {
    let refreshedAvailableQty = null;
    if (typeof window.tpsGetLiveVariantInventoryQuantity === 'function') {
      refreshedAvailableQty = window.tpsGetLiveVariantInventoryQuantity(variantId);
    }

    if (!Number.isFinite(refreshedAvailableQty) && productHandle) {
      try {
        const product = await getUpcartProductJson(productHandle, { forceRefresh: forceFreshData });
        const variant = Array.isArray(product?.variants)
          ? product.variants.find((entry) => String(entry.id) === String(variantId))
          : null;
        const ajaxAvailableQty = Number(variant?.inventory_quantity);
        if (Number.isFinite(ajaxAvailableQty)) {
          refreshedAvailableQty = ajaxAvailableQty;
        }
      } catch (error) {
        console.error('Error refreshing UpCart variant quantity:', error);
      }
    }

    if (Number.isFinite(refreshedAvailableQty)) {
      availableQty = refreshedAvailableQty;
      properties._variant_available_quantity = String(refreshedAvailableQty);
      updateHiddenPropertyValueInDOM(productItem, '_variant_available_quantity', refreshedAvailableQty);
      updatePreloadedCartProperty(itemId, '_variant_available_quantity', refreshedAvailableQty);
    }
  }

  const { cartLineItemAvailabilityMessage, nodeText } = await window.getProductAvailabilityMessages(
    properties._product_sku ?? '',
    getCurrentQuantityFromProductTile(productItem),
    availableQty,
    properties._lead_time ?? '',
    properties._is_custom_order ?? false,
    { forceRefresh: forceFreshData }
  );

  const availabilityMessage = String(cartLineItemAvailabilityMessage || nodeText || '')
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  if (!availabilityMessage) {
    syncMinQtyQuantityControls(productItem);
    return;
  }

  const refreshedQty = String(availableQty);
  const qtyChanged = refreshedQty !== storedQty;
  const messageChanged = availabilityMessage !== storedAvailability;
  let activeItemId = itemId;

  updateAvailabilityMessageInDOM(activeItemId, availabilityMessage);
  updatePreloadedCartProperty(activeItemId, 'Availability', availabilityMessage);

  // TPS-641 Upcart Availability Message should properly update even when null ATP date
  const shouldPersistAvailability =
    (qtyChanged || messageChanged) &&
    !availabilityPersistInFlight.has(itemId) &&
    !pendingRequests.has(itemId);

  if (shouldPersistAvailability) {
    const quantity = getCurrentQuantityFromProductTile(productItem);
    const propertiesToSave = {
      ...properties,
      _variant_available_quantity: refreshedQty,
      Availability: availabilityMessage,
    };

    availabilityPersistInFlight.add(itemId);
    try {
      const { updatedItemId } = await persistUpcartLineProperties(itemId, quantity, propertiesToSave, {
        productItem,
      });
      activeItemId = updatedItemId;
      syncProductTileItemId(productItem, updatedItemId);
      updateAvailabilityMessageInDOM(activeItemId, availabilityMessage);
      updatePreloadedCartProperty(activeItemId, 'Availability', availabilityMessage);
      updatePreloadedCartProperty(activeItemId, '_variant_available_quantity', refreshedQty);
    } catch (error) {
      console.error('Error persisting UpCart availability on drawer sync:', error);
    } finally {
      availabilityPersistInFlight.delete(itemId);
      if (activeItemId !== itemId) {
        availabilityPersistInFlight.delete(activeItemId);
      }
    }
  }

  syncMinQtyQuantityControls(productItem);
}

async function runInitialUpcartAvailabilitySync(options = {}) {
  const { forceFreshData = false } = options;
  beginUpcartCheckoutLock();
  try {
    await syncUpcartPreloadedCartFromShopify();

    const productItems = getCurrentUpcartProductItems();
    if (productItems.length === 0) return;

    await Promise.all(
      productItems.map((productItem) => syncInitialAvailabilityForProductTile(productItem, { forceFreshData }))
    );
    hideUnderscoreProperties();
    styleAvailabilityMessages();
  } finally {
    endUpcartCheckoutLock();
  }
}

/**
 * Best-effort lookup of the cart line item key for an UpCart product row, using only
 * data already on the page (tile id/data attributes, or the variant id in the product
 * link matched against `upcartPreloadedCart`). Does not call the network; returns
 * `null` when the key is not available yet (for example right after add-to-cart before
 * preload is populated).
 */
function getItemIdFromProductTileSync(productItem) {
  return resolveProductTileItemIdFromPreloadedCart(productItem);
}

/**
 * For UpCart line items with a minimum sale quantity (`_minimum_sale_qty`), turns the
 * decrement control off when the shown quantity is already at that minimum so shoppers
 * cannot step below it. Re-enables decrement when quantity is above the minimum.
 */
/**
 * True if the event target is the UpCart V2/V1 quantity plus or minus control (click only).
 */
function isUpcartQuantityAdjustButton(el) {
  if (!el || el.nodeType !== 1) return false;
  return (
    el.classList.contains('upcart-public-component-product-tile__quantity-plus') ||
    el.closest('.upcart-public-component-product-tile__quantity-plus') !== null ||
    el.classList.contains('upcart-public-component-product-tile__quantity-minus') ||
    el.closest('.upcart-public-component-product-tile__quantity-minus') !== null ||
    el.classList.contains('upcart-product-quantity-plus') ||
    el.closest('.upcart-product-quantity-plus') !== null ||
    el.classList.contains('upcart-product-quantity-minus') ||
    el.closest('.upcart-product-quantity-minus') !== null
  );
}

function syncMinQtyQuantityControls(productItem) {
  if (!productItem) return;

  const quantitySelector = productItem.querySelector('.upcart-public-component-product-tile__quantity-selector');
  const quantityInput = quantitySelector
    ? quantitySelector.querySelector('input[inputmode="numeric"], input[type="text"], input[type="number"]')
    : productItem.querySelector('.upcart-product-quantity-input');

  const minus =
    productItem.querySelector('.upcart-public-component-product-tile__quantity-minus') ||
    productItem.querySelector('.upcart-product-quantity-minus');

  if (!minus) return;

  const qty = quantityInput ? parseInt(quantityInput.value, 10) || 0 : 0;
  const itemId = getItemIdFromProductTileSync(productItem);
  const minQty = itemId ? getMinimumSaleQty(itemId) : 1;

  if (minQty > 1 && qty <= minQty) {
    minus.disabled = true;
    minus.setAttribute('aria-disabled', 'true');
  } else {
    minus.disabled = false;
    minus.removeAttribute('aria-disabled');
  }
}

function setupProductItemListeners(item) {
  // V2.0: Find input inside quantity selector, fallback to V1.0
  const quantitySelector = item.querySelector('.upcart-public-component-product-tile__quantity-selector');
  const quantityInput = quantitySelector
    ? quantitySelector.querySelector('input[inputmode="numeric"], input[type="text"], input[type="number"]')
    : item.querySelector('.upcart-product-quantity-input');

  // V2.0: Try new button classes, fallback to V1.0
  const v2Buttons = item.querySelectorAll('.upcart-public-component-product-tile__quantity-minus, .upcart-public-component-product-tile__quantity-plus');
  const v1Buttons = item.querySelectorAll('.upcart-product-quantity-minus, .upcart-product-quantity-plus');
  const quantityButtons = v2Buttons.length > 0 ? v2Buttons : v1Buttons;

  // Disable manual input if minimum quantity is required
  disableManualInputForItemsWithMinimumQuantity(item)
    .then(() => {
      syncMinQtyQuantityControls(item);
    })
    .catch((err) => {
      console.error('Error in disableManualInputForItemsWithMinimumQuantity:', err);
    });

  syncMinQtyQuantityControls(item);

  if (quantityInput) {
    addEventListenerSafely(quantityInput, 'change', handleQuantityChange);
  }

  quantityButtons.forEach((button) => {
    addEventListenerSafely(button, 'click', handleQuantityChange, { capture: true });
  });
}

/**
 * TPS-603: Disable manual input for items with minimum quantity requirements
 * V2.0 Migration: Updated to find input inside quantity selector and handle async itemId retrieval
 */
async function disableManualInputForItemsWithMinimumQuantity(productItem) {
  const itemId = await getItemIdFromProductTile(productItem);
  if (!itemId) return;

  const minQty = getMinimumSaleQty(itemId);

  if (minQty > 1) {
    // V2.0 Migration: Quantity input no longer has a class, must find it inside the quantity selector
    const quantitySelector = productItem.querySelector('.upcart-public-component-product-tile__quantity-selector');
    const quantityInput = quantitySelector
      ? quantitySelector.querySelector('input[inputmode="numeric"], input[type="text"], input[type="number"]')
      : productItem.querySelector('.upcart-product-quantity-input');

    if (quantityInput) {
      quantityInput.readOnly = true;
      const message = `Quantity must be in multiples of ${minQty}`;
      quantityInput.title = message;
      quantityInput.setAttribute('aria-label', message);
    }
  }
}

async function handleQuantityChange(event) {
    // Use currentTarget for +/- clicks so children (SVG, span) still resolve the tile and button classes.
    const input = event.currentTarget != null ? event.currentTarget : event.target;
    // V2.0 Migration: Updated to use .upcart-public-component-product-tile (V2.0) with fallback to .upcart-product-item (V1.0)
    const productItem = input.closest('.upcart-public-component-product-tile') ||
                       input.closest('.upcart-product-item');
    if (!productItem) {
        return;
    }

    const isQtyButtonClick = event.type === 'click' && isUpcartQuantityAdjustButton(input);

    // Phase 2: block UpCart's native +/- before any await (capture listener + stopImmediatePropagation).
    let qtyClickLocked = false;
    if (isQtyButtonClick) {
        event.preventDefault();
        event.stopImmediatePropagation();
        if (quantityTileClickInFlight.has(productItem)) {
            return;
        }
        quantityTileClickInFlight.add(productItem);
        qtyClickLocked = true;
    }

    try {
        // V2.0 Migration: getItemIdFromProductTile is now async to handle cases where preloaded cart is empty
        // When items are added to an empty cart, window.upcartPreloadedCart.items may be empty initially,
        // so we fetch the cart directly from /cart.js to get the itemId
        const itemId = await getItemIdFromProductTile(productItem, { forceFresh: true });
        if (!itemId) {
            console.error('Could not find itemId for product item');
            return;
        }

        // Prevent multiple simultaneous requests for the same item (still cancel default for +/- clicks above)
        if (pendingRequests.has(itemId)) {
            console.error('Request already in progress for item:', itemId);
            return;
        }

        const minQty = getMinimumSaleQty(itemId);
        const productSku = getProductSku(itemId);

        // If minus is clicked at the minimum quantity, skip the cart API (belt-and-suspenders with disabled minus).
        const quantitySelectorEarly = productItem.querySelector('.upcart-public-component-product-tile__quantity-selector');
        const quantityInputEarly = quantitySelectorEarly
          ? quantitySelectorEarly.querySelector('input[inputmode="numeric"], input[type="text"], input[type="number"]')
          : productItem.querySelector('.upcart-product-quantity-input');

        if (quantityInputEarly) {
          const currentValueEarly = parseInt(quantityInputEarly.value, 10) || 0;
          const isMinusAtFloor =
            input.classList.contains('upcart-public-component-product-tile__quantity-minus') ||
            input.closest('.upcart-public-component-product-tile__quantity-minus') !== null ||
            input.classList.contains('upcart-product-quantity-minus') ||
            input.closest('.upcart-product-quantity-minus') !== null;
          if (isMinusAtFloor && minQty > 1 && currentValueEarly <= minQty) {
            syncMinQtyQuantityControls(productItem);
            return;
          }
        }

        if (!isQtyButtonClick) {
            event.preventDefault();
            event.stopPropagation();
        }

        // Disable buttons during request to prevent multiple clicks
        const v2Buttons = productItem.querySelectorAll('.upcart-public-component-product-tile__quantity-minus, .upcart-public-component-product-tile__quantity-plus');
        const v1Buttons = productItem.querySelectorAll('.upcart-product-quantity-minus, .upcart-product-quantity-plus');
        const buttons = v2Buttons.length > 0 ? v2Buttons : v1Buttons;
        buttons.forEach(button => button.disabled = true);

        // V2.0 Migration: Quantity input no longer has a class, must find it inside the quantity selector
        // Input can have inputmode="numeric", type="text", or type="number"
        const quantitySelector = productItem.querySelector('.upcart-public-component-product-tile__quantity-selector');
        const quantityInput = quantitySelector
          ? quantitySelector.querySelector('input[inputmode="numeric"], input[type="text"], input[type="number"]')
          : productItem.querySelector('.upcart-product-quantity-input');

        if (!quantityInput) {
            buttons.forEach(button => button.disabled = false);
            return;
        }

        let currentValue = parseInt(quantityInput.value, 10) || 0;

        // V2.0 Migration: Updated button class selectors to use V2.0 classes with V1.0 fallback
        const isPlus = input.classList.contains('upcart-public-component-product-tile__quantity-plus') ||
            input.closest('.upcart-public-component-product-tile__quantity-plus') !== null ||
            input.classList.contains('upcart-product-quantity-plus') ||
            input.closest('.upcart-product-quantity-plus') !== null;
        const isMinus = input.classList.contains('upcart-public-component-product-tile__quantity-minus') ||
            input.closest('.upcart-public-component-product-tile__quantity-minus') !== null ||
            input.classList.contains('upcart-product-quantity-minus') ||
            input.closest('.upcart-product-quantity-minus') !== null;

        let newQuantity;
        if (isPlus) {
            newQuantity = currentValue + minQty;
        } else if (isMinus) {
            newQuantity = Math.max(0, currentValue - minQty);
        } else {
            // For manual input, round to nearest multiple of minQty
            newQuantity = Math.round(currentValue / minQty) * minQty;
        }

        // Ensure newQuantity is a valid number and not less than minQty
        if (isNaN(newQuantity) || newQuantity < minQty) {
            newQuantity = minQty;
        }

        // Ensure quantity is always a multiple of minQty (prevents odd quantities when minQty is 2)
        if (newQuantity > 0 && newQuantity % minQty !== 0) {
            newQuantity = Math.round(newQuantity / minQty) * minQty;
            // Ensure it's still at least minQty
            if (newQuantity < minQty) {
                newQuantity = minQty;
            }
        }

        let resolvedItemId = itemId;
        try {
            // Mark request as in progress
            pendingRequests.set(itemId, true);

            await updateCartQuantity(itemId, newQuantity, productSku, productItem);

            const tileAfterUpdate = findProductTileByItemId(itemId) || productItem;
            resolvedItemId =
              tileAfterUpdate?.dataset?.itemId ||
              tileAfterUpdate?.dataset?.itemKey ||
              tileAfterUpdate?.id ||
              itemId;

            // Re-enable buttons after successful update
            buttons.forEach(button => button.disabled = false);
            syncMinQtyQuantityControls(productItem);
        } catch (error) {
            console.error('Error updating cart quantity:', error);
            // Re-enable buttons after error
            buttons.forEach(button => button.disabled = false);
            syncMinQtyQuantityControls(productItem);
        } finally {
            // Remove from pending requests after a delay to allow cart to refresh
            const cleanupItemId = itemId;
            const cleanupResolvedItemId = resolvedItemId;
            setTimeout(() => {
                pendingRequests.delete(cleanupItemId);
                if (cleanupResolvedItemId !== cleanupItemId) {
                  pendingRequests.delete(cleanupResolvedItemId);
                }
                const tile =
                  findProductTileByItemId(cleanupResolvedItemId) ||
                  findProductTileByItemId(cleanupItemId) ||
                  productItem;
                syncMinQtyQuantityControls(tile);
            }, 500);
        }
    } finally {
        if (qtyClickLocked) {
            quantityTileClickInFlight.delete(productItem);
        }
    }
}

// TPS-641 Upcart Availability Message should properly update even when null ATP date
async function persistUpcartLineProperties(itemId, quantity, properties, options = {}) {
  const { productItem = null } = options;
  let activeItemId = itemId;

  async function postCartChange(id) {
    return fetch('/cart/change.js?upcart=1', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        quantity,
        id,
        properties,
      }),
    });
  }

  let response = await postCartChange(activeItemId);
  let data = await response.json();

  if (
    !response.ok &&
    response.status === 400 &&
    String(data?.description || data?.message || '').includes('no valid id')
  ) {
    const liveItemId = await findLiveCartLineKey(activeItemId, productItem);
    if (liveItemId && liveItemId !== activeItemId) {
      migrateUpcartLineKeyReferences(activeItemId, liveItemId);
      if (productItem) {
        syncProductTileItemId(productItem, liveItemId);
      }
      activeItemId = liveItemId;
      response = await postCartChange(activeItemId);
      data = await response.json();
    }
  }

  if (!response.ok) {
    console.error('Cart property update failed:', response.status, data);
    throw new Error(`Cart property update failed: ${response.status}`);
  }

  let updatedItemId = activeItemId;
  if (data.items && data.items.length > 0) {
    const variantId = activeItemId.split(':')[0];
    const responseItem = data.items.find((item) => {
      const responseVariantId = item.key ? item.key.split(':')[0] : null;
      return responseVariantId == variantId ||
        (item.variant_id && item.variant_id == variantId);
    });

    if (responseItem?.key) {
      updatedItemId = responseItem.key;
    }
  }

  if (updatedItemId !== activeItemId) {
    migrateUpcartLineKeyReferences(activeItemId, updatedItemId);
    if (productItem) {
      syncProductTileItemId(productItem, updatedItemId);
    }
  }

  if (window.upcartPreloadedCart?.items) {
    if (updatedItemId !== activeItemId) {
      const oldIndex = window.upcartPreloadedCart.items.findIndex((item) => item.key === activeItemId);
      if (oldIndex !== -1) {
        window.upcartPreloadedCart.items.splice(oldIndex, 1);
      }
    }

    const itemIndex = window.upcartPreloadedCart.items.findIndex((item) => item.key === updatedItemId);
    if (itemIndex !== -1) {
      window.upcartPreloadedCart.items[itemIndex].quantity = quantity;
      if (window.upcartPreloadedCart.items[itemIndex].properties) {
        Object.assign(window.upcartPreloadedCart.items[itemIndex].properties, properties);
      }
    } else if (data.items) {
      const responseItem = data.items.find((item) => item.key === updatedItemId);
      if (responseItem) {
        window.upcartPreloadedCart.items.push(responseItem);
      }
    }
  }

  return { data, updatedItemId };
}

// Function to update cart quantity via Shopify API
async function updateCartQuantity(itemId, quantity, sku = null, productItem = null) {
  beginUpcartCheckoutLock();
  // Validate quantity before API call
  const minQty = getMinimumSaleQty(itemId);
  if (quantity > 0 && quantity % minQty !== 0) {
    // Round to nearest valid multiple
    quantity = Math.round(quantity / minQty) * minQty;
    // Ensure it's still at least minQty
    if (quantity < minQty) {
      quantity = minQty;
    }
  }

  // Get properties and ensure it's an object
  const properties = getProperties(itemId) || {};
  const availabilityMessage = await getProductAvailabilityMessage(itemId, quantity, properties);

  // Set the availability message
  if (availabilityMessage) {
    properties.Availability = availabilityMessage;
  }

  try {
    const { data, updatedItemId } = await persistUpcartLineProperties(itemId, quantity, properties, {
      productItem,
    });

    // Update the input value directly using the updated itemId
    const resolvedProductItem =
      productItem || findProductTileByItemId(updatedItemId) || findProductTileByItemId(itemId);
    if (resolvedProductItem) {
      syncProductTileItemId(resolvedProductItem, updatedItemId);
      // V2.0: Find input inside quantity selector, fallback to V1.0
      const quantitySelector = resolvedProductItem.querySelector('.upcart-public-component-product-tile__quantity-selector');
      const quantityInput = quantitySelector
        ? quantitySelector.querySelector('input[inputmode="numeric"], input[type="text"], input[type="number"]')
        : resolvedProductItem.querySelector('.upcart-product-quantity-input');
      if (quantityInput) {
        quantityInput.value = quantity;
      }
    }
    // https://aftersell.notion.site/UpCart-Public-API-7a0f6d75cb044871bdb6da5d99cfc755#7853598f7abe4f64be273db45f231282
    window.upcartRefreshCart();

    // V2.0 Migration: After cart refresh, update the availability message in the DOM
    // Use updatedItemId in case it changed
    await new Promise((resolve) => {
      setTimeout(() => {
        updateAvailabilityMessageInDOM(updatedItemId, availabilityMessage);
        styleAvailabilityMessages(); // Remove "Availability: " label if present
        syncUpcartCheckoutButtonState();
        const tileAfterRefresh = findProductTileByItemId(updatedItemId);
        if (tileAfterRefresh) {
          syncMinQtyQuantityControls(tileAfterRefresh);
        }
        resolve();
      }, 200);
    });

    return data;
  } catch (error) {
    throw error;
  } finally {
    endUpcartCheckoutLock();
  }
}

async function getProductAvailabilityMessage(itemId, itemQuantity, properties) {
  const isCustom = properties._is_custom_order == "true" ? true : false;
  const variantLeadTime = properties._lead_time ?? null;
  const variantSku = properties._product_sku ?? null;
  const availableQty = properties._variant_available_quantity ?? 0;

  // Get the product item
  const productItem = findProductTileByItemId(itemId);
  if (!productItem) return null;

  // V2.0 Migration: Updated to use V2.0 class with V1.0 fallback
  const productLink = productItem.querySelector('.upcart-public-component-product-tile__title-link, .upcart-product-title-link');
  if (!productLink) return null;

  // Extract variant ID from the URL
  const variantId = new URL(productLink.href).searchParams.get('variant');
  if (!variantId) return null;

  // Extract product handle from the URL
  const productHandle = productLink.href.split('/products/')[1].split('?')[0];
  if (!productHandle) return null;

  try {
    // Fetch product data
    const response = await fetch(`/products/${productHandle}.js`);
    const product = await response.json();

    // Get the variant
    const variant = product.variants.find(v => v.id == variantId);
    if (!variant) return null;

    // Determine availability message
    const { cartLineItemAvailabilityMessage } = await window.getProductAvailabilityMessages(variantSku, itemQuantity, availableQty, variantLeadTime, isCustom);

    return cartLineItemAvailabilityMessage;
  } catch (error) {
    console.error('Error fetching product data:', error);
    return null;
  }
}

function getProperties(itemId) {
  let properties = {};

  // First try to get properties from preloaded cart
  const originalItem = window.upcartPreloadedCart?.items?.find(item => item.key === itemId);
  if (originalItem?.properties) {
    properties = { ...originalItem.properties };
  } else {
    // If not in preloaded cart, get properties from DOM
    const productItem = findProductTileByItemId(itemId);
    if (productItem) {
      // V2.0: Try new class structure first
      const v2PropertyElements = productItem.querySelectorAll('.upcart-public-product-properties__item');
      v2PropertyElements.forEach(property => {
        const keyValuePair = property.querySelector('.upcart-public-cart-items__key-value-pair');
        if (keyValuePair) {
          const text = keyValuePair.textContent.trim();
          const colonIndex = text.indexOf(':');
          if (colonIndex > 0) {
            const key = text.substring(0, colonIndex).trim();
            const value = text.substring(colonIndex + 1).trim();
            properties[key] = value;
          }
        }
      });

      // V1.0 fallback: Try old class structure
      if (Object.keys(properties).length === 0) {
        const v1PropertyElements = productItem.querySelectorAll('.upcart-item-property');
        v1PropertyElements.forEach(property => {
          const strongElement = property.querySelector('strong');
          const spanElement = property.querySelector('span');
          if (strongElement && spanElement) {
            const key = strongElement.textContent.replace(':', '').trim();
            const value = spanElement.textContent.trim();
            properties[key] = value;
          }
        });
      }
    }
  }

  return properties;
}

function getMinimumSaleQty(itemId) {
  // First check if we have the item in preloaded cart
  if (window?.upcartPreloadedCart?.items) {
    const item = window.upcartPreloadedCart.items.find(item => item.key === itemId);
    if (item?.properties?._minimum_sale_qty) {
      const minQty = parseInt(item.properties._minimum_sale_qty, 10);

      return minQty;
    }
  }

  // If not in preloaded cart, try to get from DOM
  const productItem = findProductTileByItemId(itemId);

  if (productItem) {
    // V2.0: Try new class structure first
    const v2HiddenProperties = productItem.querySelectorAll('.upcart-public-product-properties__item.hidden');
    for (const property of v2HiddenProperties) {
      const keyValuePair = property.querySelector('.upcart-public-cart-items__key-value-pair');
      if (keyValuePair && keyValuePair.textContent.includes('_minimum_sale_qty:')) {
        const text = keyValuePair.textContent.trim();
        const colonIndex = text.indexOf(':');
        if (colonIndex > 0) {
          const value = text.substring(colonIndex + 1).trim();
          const minQty = parseInt(value, 10);
          if (!isNaN(minQty)) {
            return minQty;
          }
        }
      }
    }

    // V1.0 fallback
    const v1HiddenProperties = productItem.querySelectorAll('.upcart-item-property.hidden');
    for (const property of v1HiddenProperties) {
      const strongElement = property.querySelector('strong');
      if (strongElement && strongElement.textContent.includes('_minimum_sale_qty:')) {
        const minQtySpan = property.querySelector('span');
        if (minQtySpan) {
          const minQty = parseInt(minQtySpan.textContent.trim(), 10);
          return minQty;
        }
      }
    }
  }

  return 1;
}

function getProductSku(itemId) {
  // First check if we have the item in preloaded cart
  if (window?.upcartPreloadedCart?.items) {
    const item = window.upcartPreloadedCart.items.find(item => item.key === itemId);
    if (item?.properties?._product_sku) {
      const sku = item.properties._product_sku;

      return sku;
    }
  }

  // If not in preloaded cart, try to get from DOM
  const productItem = findProductTileByItemId(itemId);

  if (productItem) {
    // V2.0: Try new class structure first
    const v2HiddenProperties = productItem.querySelectorAll('.upcart-public-product-properties__item.hidden');
    for (const property of v2HiddenProperties) {
      const keyValuePair = property.querySelector('.upcart-public-cart-items__key-value-pair');
      if (keyValuePair && keyValuePair.textContent.includes('_product_sku:')) {
        const text = keyValuePair.textContent.trim();
        const colonIndex = text.indexOf(':');
        if (colonIndex > 0) {
          const sku = text.substring(colonIndex + 1).trim();
          return sku;
        }
      }
    }

    // V1.0 fallback
    const v1HiddenProperties = productItem.querySelectorAll('.upcart-item-property.hidden');
    for (const property of v1HiddenProperties) {
      const strongElement = property.querySelector('strong');
      if (strongElement && strongElement.textContent.includes('_product_sku:')) {
        const skuSpan = property.querySelector('span');
        if (skuSpan) {
          const sku = skuSpan.textContent.trim();
          return sku;
        }
      }
    }
  }

  return null;
}

/**
 * Get itemId from product tile - handles both V1.0 and V2.0 structures
 *
 * V2.0 Migration Notes:
 * - V2.0 product tiles may not have an id attribute
 * - We match by variant ID from the product link URL
 * - When preloaded cart is empty (e.g., after adding first item), we fetch cart from /cart.js
 * - This ensures we can get the itemId even when window.upcartPreloadedCart.items is empty
 *
 * @param {HTMLElement} productItem - The product tile element
 * @param {Object} [options]
 * @param {boolean} [options.forceFresh=false] - Refresh from `/cart.js` before resolving
 * @returns {Promise<string|null>} The itemId (cart line item key) or null if not found
 */
async function getItemIdFromProductTile(productItem, options = {}) {
  const { forceFresh = false } = options;

  if (forceFresh) {
    await syncUpcartPreloadedCartFromShopify();
  }

  const resolvedFromPreload = resolveProductTileItemIdFromPreloadedCart(productItem);
  if (resolvedFromPreload) {
    return resolvedFromPreload;
  }

  const variantId = getVariantIdFromTile(productItem);
  if (!variantId) {
    return null;
  }

  if (!forceFresh) {
    try {
      const response = await fetch('/cart.js');
      if (response.ok) {
        const cart = await response.json();
        if (cart.items && cart.items.length > 0) {
          if (window.upcartPreloadedCart && typeof window.upcartPreloadedCart === 'object') {
            window.upcartPreloadedCart.items = cart.items;
          } else {
            window.upcartPreloadedCart = cart;
          }

          const itemKey = findCartLineKeyForVariantId(variantId, cart.items);
          if (itemKey) {
            syncProductTileItemId(productItem, itemKey);
            return itemKey;
          }
        }
      }
    } catch (error) {
      console.error('Error fetching cart:', error);
    }
  }

  return null;
}

/**
 * Find product tile by itemId - handles both V1.0 and V2.0 structures
 */
function findProductTileByItemId(itemId) {
  // Try V2.0 first
  const v2Tile = document.querySelector(`.upcart-public-component-product-tile[id="${itemId}"]`);
  if (v2Tile) return v2Tile;

  // Try V1.0
  const v1Tile = document.querySelector(`.upcart-product-item[id="${itemId}"]`);
  if (v1Tile) return v1Tile;

  // Try data attributes
  const dataTile = document.querySelector(`.upcart-public-component-product-tile[data-item-id="${itemId}"], .upcart-public-component-product-tile[data-item-key="${itemId}"]`);
  if (dataTile) return dataTile;

  // Try to find by matching with preloaded cart
  if (window?.upcartPreloadedCart?.items) {
    const cartItem = window.upcartPreloadedCart.items.find(item => item.key === itemId);
    if (cartItem) {
      // Try to find tile by variant ID
      const variantId = cartItem.variant_id || cartItem.variant?.id;
      if (variantId) {
        const allTiles = document.querySelectorAll('.upcart-public-component-product-tile, .upcart-product-item');
        for (const tile of allTiles) {
          const productLink = tile.querySelector('.upcart-product-title-link, .upcart-public-component-product-tile__title-link');
          if (productLink) {
            const tileVariantId = new URL(productLink.href).searchParams.get('variant');
            if (tileVariantId == variantId) {
              return tile;
            }
          }
        }
      }
    }
  }

  return null;
}

/**
 * Update the availability message in the DOM after cart refresh
 * V2.0 Migration: Finds the availability message element and updates its text
 *
 * @param {string} itemId - The cart line item key
 * @param {string} availabilityMessage - The new availability message text
 */
// TPS-641 Upcart Availability Message should properly update even when null ATP date
function updateAvailabilityMessageInDOM(itemId, availabilityMessage) {
  if (!availabilityMessage) return;

  const productItem = findProductTileByItemId(itemId);
  if (!productItem) return;

  const cleanMessage = availabilityMessage.replace(/^Availability:\s*/i, '');
  const availabilityElement = findAvailabilityMessageElement(productItem);

  if (availabilityElement) {
    availabilityElement.textContent = cleanMessage;
    availabilityElement.classList.add('availability-message-text');
    return;
  }

  const availabilityElements = productItem.querySelectorAll('.upcart-public-cart-items__key-value-pair');

  availabilityElements.forEach(element => {
    const text = element.textContent.trim();
    const parentItem = element.closest('.upcart-public-product-properties__item');
    const parentText = parentItem ? parentItem.textContent.trim() : '';

    if (text.startsWith('Availability:') ||
        text === availabilityMessage ||
        parentText.includes('Availability')) {
      element.textContent = cleanMessage;
      element.classList.add('availability-message-text');
    }
  });
}

function addEventListenerSafely(element, eventType, handler, options) {
  element.removeEventListener(eventType, handler, options);
  element.addEventListener(eventType, handler, options);
}

